import { useEffect, useState } from "react";
import { X, Gear, Building, Bell, Scales, ArrowsClockwise, CheckCircle, Plug } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { companyApi, userPrefsApi, reportsApi, http } from "@/lib/api";
import IntegrationsTab from "./IntegrationsTab";

const TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
];

export default function SettingsDialog({ onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [tab, setTab] = useState("company");
  const [company, setCompany] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([companyApi.me().catch(() => null), userPrefsApi.getNotificationPrefs().catch(() => null)])
      .then(([c, p]) => { setCompany(c); setPrefs(p); });
  }, []);

  const updateCompanySettings = async (partial) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const updated = await companyApi.updateSettings(partial);
      setCompany(updated);
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updatePrefs = async (partial) => {
    setSaving(true);
    try {
      const updated = await userPrefsApi.updateNotificationPrefs({ ...prefs, ...partial });
      setPrefs(updated);
      toast.success("Preference saved");
    } catch (e) {
      toast.error("Failed to save preference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="settings-dialog">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0d0d0d] border border-[#2a2a2a] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-[#0d0d0d] border-b border-[#2a2a2a] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gear size={20} className="text-[#ff8c00]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Configuration</div>
              <div className="font-heading font-black tracking-tight">Settings</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] flex items-center justify-center" data-testid="settings-close">
            <X size={14} />
          </button>
        </div>

        <div className="flex border-b border-[#2a2a2a]">
          {[
            { id: "company", label: "Company", icon: Building, adminOnly: false },
            { id: "notifications", label: "Notifications", icon: Bell, adminOnly: false },
            { id: "dispatch", label: "Dispatch Gate", icon: Scales, adminOnly: true },
            { id: "integrations", label: "Integrations", icon: Plug, adminOnly: true },
          ].map((t) => {
            if (t.adminOnly && !isAdmin) return null;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 px-3 py-3 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2 transition-colors ${
                  active ? "text-[#ff8c00] bg-[#ff8c00]/10 border-b-2 border-[#ff8c00]" : "text-[#a1a1aa] hover:text-white"
                }`}
                data-testid={`settings-tab-${t.id}`}
              >
                <t.icon size={12} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {!company && <div className="text-xs text-[#71717a] uppercase tracking-[0.2em]">Loading…</div>}

          {tab === "company" && company && (
            <CompanyTab company={company} isAdmin={isAdmin} />
          )}

          {tab === "notifications" && company && prefs && (
            <NotificationsTab company={company} prefs={prefs} updatePrefs={updatePrefs} updateCompanySettings={updateCompanySettings} isAdmin={isAdmin} saving={saving} />
          )}

          {tab === "dispatch" && company && isAdmin && (
            <DispatchTab company={company} updateCompanySettings={updateCompanySettings} saving={saving} />
          )}

          {tab === "integrations" && isAdmin && (
            <IntegrationsTab />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-[#2a2a2a] last:border-b-0">
      <div className="flex-1">
        <div className="text-sm font-semibold">{label}</div>
        {hint && <div className="text-[11px] text-[#71717a] mt-0.5 leading-relaxed">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`w-12 h-6 border flex items-center transition-colors ${
        checked ? "bg-[#ff8c00] border-[#ff8c00]" : "bg-[#1a1a1a] border-[#2a2a2a]"
      }`}
    >
      <span className={`block w-4 h-4 bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`}></span>
    </button>
  );
}

function CompanyTab({ company, isAdmin }) {
  return (
    <div className="space-y-2">
      <Row label="Company">
        <div className="text-right">
          <div className="font-heading font-bold">{company.name}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono">{company.slug}</div>
        </div>
      </Row>
      <Row label="Your Role">
        <span className="px-2 py-1 border border-[#4682b4] text-[#4682b4] text-[10px] uppercase tracking-[0.2em] font-bold">
          {isAdmin ? "Admin" : "Dispatcher"}
        </span>
      </Row>
      <Row label="Created" hint="Multi-tenant isolation: every asset, job, zone, alert, photo, and push subscription is scoped to this company.">
        <span className="text-xs font-mono text-[#a1a1aa]">{new Date(company.created_at).toLocaleDateString()}</span>
      </Row>
    </div>
  );
}

function NotificationsTab({ company, prefs, updatePrefs, updateCompanySettings, isAdmin, saving }) {
  const s = company.settings || {};
  const usingDefaults = prefs.use_company_defaults !== false;
  const effective = {
    enabled: usingDefaults ? s.quiet_hours_enabled : (prefs.quiet_hours_enabled ?? s.quiet_hours_enabled),
    start: usingDefaults ? s.quiet_hours_start : (prefs.quiet_hours_start || s.quiet_hours_start),
    end: usingDefaults ? s.quiet_hours_end : (prefs.quiet_hours_end || s.quiet_hours_end),
    tz: usingDefaults ? s.quiet_hours_timezone : (prefs.quiet_hours_timezone || s.quiet_hours_timezone),
  };

  return (
    <div className="space-y-2">
      {isAdmin && (
        <>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold pb-1 border-b border-[#2a2a2a]">Company Defaults</div>
          <Row label="Quiet hours" hint="Suppress push notifications during these hours. Critical alerts override this if enabled below.">
            <Toggle
              checked={!!s.quiet_hours_enabled}
              onChange={(v) => updateCompanySettings({ quiet_hours_enabled: v })}
              testId="company-quiet-hours-toggle"
            />
          </Row>
          <Row label="Window">
            <div className="flex items-center gap-1 font-mono text-xs">
              <input
                type="time"
                value={s.quiet_hours_start || "22:00"}
                onChange={(e) => updateCompanySettings({ quiet_hours_start: e.target.value })}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 w-24"
                data-testid="company-quiet-hours-start"
              />
              <span className="text-[#71717a]">→</span>
              <input
                type="time"
                value={s.quiet_hours_end || "05:00"}
                onChange={(e) => updateCompanySettings({ quiet_hours_end: e.target.value })}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 w-24"
                data-testid="company-quiet-hours-end"
              />
            </div>
          </Row>
          <Row label="Timezone">
            <select
              value={s.quiet_hours_timezone || "America/Los_Angeles"}
              onChange={(e) => updateCompanySettings({ quiet_hours_timezone: e.target.value })}
              className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 text-xs font-mono"
              data-testid="company-quiet-hours-tz"
            >
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Row>
          <Row label="Allow critical alerts to override" hint="Geofence breaches and overload alerts will still deliver even during quiet hours.">
            <Toggle
              checked={s.quiet_hours_critical_override !== false}
              onChange={(v) => updateCompanySettings({ quiet_hours_critical_override: v })}
              testId="company-critical-override"
            />
          </Row>
        </>
      )}

      <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold pb-1 border-b border-[#2a2a2a] pt-3">Your Override</div>
      <Row label="Use company defaults" hint="When ON, your quiet hours follow the company settings above. Turn OFF to use your own schedule.">
        <Toggle
          checked={usingDefaults}
          onChange={(v) => updatePrefs({ use_company_defaults: v })}
          testId="user-use-defaults"
        />
      </Row>
      {!usingDefaults && (
        <>
          <Row label="My quiet hours">
            <Toggle
              checked={!!prefs.quiet_hours_enabled}
              onChange={(v) => updatePrefs({ quiet_hours_enabled: v })}
              testId="user-quiet-toggle"
            />
          </Row>
          <Row label="My window">
            <div className="flex items-center gap-1 font-mono text-xs">
              <input
                type="time"
                value={prefs.quiet_hours_start || "22:00"}
                onChange={(e) => updatePrefs({ quiet_hours_start: e.target.value })}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 w-24"
                data-testid="user-quiet-start"
              />
              <span className="text-[#71717a]">→</span>
              <input
                type="time"
                value={prefs.quiet_hours_end || "05:00"}
                onChange={(e) => updatePrefs({ quiet_hours_end: e.target.value })}
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 w-24"
                data-testid="user-quiet-end"
              />
            </div>
          </Row>
          <Row label="My timezone">
            <select
              value={prefs.quiet_hours_timezone || "America/Los_Angeles"}
              onChange={(e) => updatePrefs({ quiet_hours_timezone: e.target.value })}
              className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 text-xs font-mono"
              data-testid="user-quiet-tz"
            >
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Row>
        </>
      )}

      <div className="mt-4 p-3 border border-[#4682b4]/40 bg-[#4682b4]/5 text-[11px] text-[#a1a1aa] font-mono leading-relaxed" data-testid="effective-window">
        <CheckCircle size={11} className="inline mr-1 text-[#4682b4]" weight="fill" />
        {effective.enabled
          ? <>Quiet hours <strong className="text-white">enabled</strong>: <strong className="text-white">{effective.start} → {effective.end}</strong> ({effective.tz})</>
          : <>Quiet hours <strong className="text-white">disabled</strong> — every push will deliver immediately.</>}
      </div>
      {saving && <div className="text-[10px] text-[#71717a] uppercase tracking-[0.2em] mt-2 flex items-center gap-1"><ArrowsClockwise className="animate-spin" size={10} /> Saving…</div>}

      {isAdmin && (
        <DailyReportPrefsSection
          company={company}
          updateCompanySettings={updateCompanySettings}
        />
      )}

      {isAdmin && (
        <CustomerNotificationsSection
          company={company}
          updateCompanySettings={updateCompanySettings}
        />
      )}

      {isAdmin && (
        <SlackAlertsSection
          company={company}
          updateCompanySettings={updateCompanySettings}
        />
      )}
    </div>
  );
}

function SlackAlertsSection({ company, updateCompanySettings }) {
  const initialUrl = company.settings?.slack_webhook_url || "";
  const initialEnabled = company.settings?.slack_alert_on_negative_nps ?? true;
  const [url, setUrl] = useState(initialUrl);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const isSlackUrl = !url || /^https:\/\/hooks\.slack\.com\/services\/[A-Z0-9/]+$/i.test(url.trim());

  const save = async () => {
    setSaving(true);
    try {
      await updateCompanySettings({
        slack_webhook_url: (url || "").trim() || null,
        slack_alert_on_negative_nps: enabled,
      });
      toast.success("Slack settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await http.post("/feedback/slack-test");
      toast.success("Test alert sent — check Slack!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#2a2a2a]" data-testid="slack-alerts-section">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1 h-4 bg-[#4682b4]" />
        <h3 className="font-heading font-bold text-sm tracking-tight uppercase">Slack Alerts — Negative POD Feedback</h3>
      </div>
      <p className="text-[11px] text-[#a1a1aa] leading-relaxed mb-3">
        Pipe customer 👎 ratings into a Slack channel so they don't sit unread. Paste an
        <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="text-[#4682b4] hover:underline mx-1">Incoming Webhook URL</a>
        (starts with <code className="text-[#4682b4]">https://hooks.slack.com/services/…</code>).
      </p>
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX"
        className={`w-full px-3 py-2 bg-[#1a1a1a] border outline-none text-[11px] font-mono mb-2 ${
          isSlackUrl ? "border-[#2a2a2a] focus:border-[#4682b4]" : "border-[#ef4444]"
        }`}
        data-testid="slack-webhook-input"
      />
      {!isSlackUrl && (
        <div className="text-[10px] text-[#ef4444] mb-2 font-mono">
          URL must start with https://hooks.slack.com/services/
        </div>
      )}
      <label className="flex items-center gap-3 cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-4 h-4 accent-[#4682b4]"
          data-testid="slack-enabled-toggle"
        />
        <span className="text-[11px] uppercase tracking-[0.2em] font-bold">
          Send alerts on 👎 feedback
        </span>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={save}
          disabled={saving || !isSlackUrl}
          className="bg-[#4682b4] hover:bg-[#4682b4]/90 disabled:opacity-40 text-black font-bold py-2 text-[10px] uppercase tracking-[0.2em]"
          data-testid="slack-save"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={test}
          disabled={testing || !initialUrl}
          className="border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-black disabled:opacity-40 font-bold py-2 text-[10px] uppercase tracking-[0.2em]"
          title={!initialUrl ? "Save a webhook URL first" : "Send a test alert"}
          data-testid="slack-test"
        >
          {testing ? "Sending…" : "Send Test"}
        </button>
      </div>
    </div>
  );
}

function CustomerNotificationsSection({ company, updateCompanySettings }) {
  const enabled = !!company.settings?.auto_email_pod_on_complete;
  const toggle = (val) => updateCompanySettings({ auto_email_pod_on_complete: val });
  return (
    <div className="mt-6 pt-6 border-t border-[#2a2a2a]" data-testid="customer-notifications-section">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1 h-4 bg-[#22c55e]" />
        <h3 className="font-heading font-bold text-sm tracking-tight uppercase">Customer POD Email</h3>
      </div>
      <p className="text-[11px] text-[#a1a1aa] leading-relaxed mb-3">
        When a driver marks a stop complete, automatically email the watermarked
        proof-of-delivery photo and a verified GPS pin to the customer (uses the
        <code className="text-[#22c55e]"> Customer Email</code> field on each job).
        Tamper-evident, sent the moment the stop turns green.
      </p>
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
          className="w-4 h-4 accent-[#22c55e]"
          data-testid="auto-email-pod-toggle"
        />
        <span className="text-[11px] uppercase tracking-[0.2em] font-bold">
          {enabled ? <span className="text-[#22c55e]">Auto-POD email enabled</span> : <span className="text-[#71717a]">Off — POD email disabled</span>}
        </span>
      </label>
      <div className="mt-3 text-[10px] text-[#71717a] font-mono">
        Jobs without a customer_email are silently skipped. Re-completing an already-complete stop does NOT re-email.
      </div>
    </div>
  );
}

function DailyReportPrefsSection({ company, updateCompanySettings }) {
  const initial = company.settings?.daily_report_prefs || {};
  const defaults = {
    include_summary: true, include_jobs: true, include_assets: true,
    include_compliance: true, include_routes: true, include_maintenance: true,
    include_pod_appendix: true,
    auto_email_enabled: false, auto_email_time: "18:00",
    auto_email_timezone: company.settings?.quiet_hours_timezone || "America/Los_Angeles",
    recipients: [],
  };
  const current = { ...defaults, ...initial };
  const [recipientsText, setRecipientsText] = useState((current.recipients || []).join(", "));
  const [testing, setTesting] = useState(false);

  const SECTIONS = [
    { key: "include_summary", label: "Daily Summary" },
    { key: "include_jobs", label: "Jobs Table" },
    { key: "include_assets", label: "Fleet Roster" },
    { key: "include_compliance", label: "Compliance Check" },
    { key: "include_routes", label: "Routes for the Day" },
    { key: "include_maintenance", label: "Maintenance Due" },
    { key: "include_pod_appendix", label: "POD Appendix (GPS-stamped photos)" },
  ];

  const toggle = (key) => {
    const next = { ...current, [key]: !current[key] };
    updateCompanySettings({ daily_report_prefs: next });
  };
  const setField = (key, value) => {
    const next = { ...current, [key]: value };
    updateCompanySettings({ daily_report_prefs: next });
  };
  const commitRecipients = () => {
    const list = recipientsText.split(/[,\n;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    setField("recipients", list);
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await reportsApi.sendDailyTest();
      toast.success(`Test sent to ${r?.recipients?.length || 0} recipient(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#2a2a2a] space-y-5" data-testid="daily-report-prefs-section">
      <div>
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">Daily Report Defaults</div>
          <p className="text-[11px] text-[#a1a1aa] mt-1 leading-relaxed">
            Toggle which sections appear by default when someone clicks <strong className="text-white">Daily Report</strong> on the Jobs page. Dispatchers can still override per-report from the Report Builder dialog.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {SECTIONS.map((s) => (
            <label key={s.key} className={`flex items-center gap-2 border p-2 cursor-pointer transition-colors ${current[s.key] ? "border-[#ff8c00] bg-[#ff8c00]/5" : "border-[#2a2a2a] hover:bg-[#1a1a1a]"}`} data-testid={`report-prefs-${s.key}`}>
              <input
                type="checkbox"
                checked={!!current[s.key]}
                onChange={() => toggle(s.key)}
                className="accent-[#ff8c00]"
                data-testid={`report-prefs-toggle-${s.key}`}
              />
              <span className="text-xs font-bold">{s.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">Scheduled Email</div>
            <p className="text-[11px] text-[#a1a1aa] mt-1 leading-relaxed">
              Mail the day's PDF every evening — set it and forget it.
            </p>
          </div>
          <button
            type="button"
            onClick={sendTest}
            disabled={testing}
            className="border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4]/10 disabled:opacity-40 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1"
            data-testid="daily-email-test"
          >
            {testing ? <ArrowsClockwise size={11} className="animate-spin" /> : null}
            Send Test Now
          </button>
        </div>
        <label className="flex items-center gap-2 cursor-pointer border border-[#2a2a2a] p-3" data-testid="daily-email-enabled">
          <input
            type="checkbox"
            checked={current.auto_email_enabled}
            onChange={(e) => setField("auto_email_enabled", e.target.checked)}
            className="accent-[#ff8c00]"
            data-testid="daily-email-enabled-toggle"
          />
          <span className="text-xs font-bold">Email the PDF automatically</span>
        </label>
        <div className={`grid grid-cols-2 gap-2 mt-2 ${!current.auto_email_enabled ? "opacity-50" : ""}`}>
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#71717a] font-bold mb-1">Send Time (local)</div>
            <input
              type="time"
              value={current.auto_email_time}
              disabled={!current.auto_email_enabled}
              onChange={(e) => setField("auto_email_time", e.target.value)}
              className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono w-full"
              data-testid="daily-email-time"
            />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#71717a] font-bold mb-1">Timezone</div>
            <input
              type="text"
              value={current.auto_email_timezone || ""}
              disabled={!current.auto_email_enabled}
              onChange={(e) => setField("auto_email_timezone", e.target.value)}
              className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono w-full"
              placeholder="America/Los_Angeles"
              data-testid="daily-email-tz"
            />
          </div>
        </div>
        <div className="mt-2">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#71717a] font-bold mb-1">Recipients (comma-separated; empty = all admins)</div>
          <input
            type="text"
            value={recipientsText}
            disabled={!current.auto_email_enabled}
            onChange={(e) => setRecipientsText(e.target.value)}
            onBlur={commitRecipients}
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono w-full"
            placeholder="dispatch@yourshop.com, ops-manager@yourshop.com"
            data-testid="daily-email-recipients"
          />
        </div>
      </div>
    </div>
  );
}

function DispatchTab({ company, updateCompanySettings, saving }) {
  const s = company.settings || {};
  return (
    <div className="space-y-2">
      <Row
        label="Dispatch-gate mode"
        hint="When the Federal Bridge Formula fails, BLOCK refuses dispatch unless a permit override is acknowledged. WARN shows the violation but lets dispatchers proceed."
      >
        <div className="flex border border-[#2a2a2a]">
          {["warn", "block"].map((m) => (
            <button
              key={m}
              onClick={() => updateCompanySettings({ dispatch_gate_mode: m })}
              className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold ${
                s.dispatch_gate_mode === m ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:text-white"
              }`}
              data-testid={`gate-mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      </Row>
      {saving && <div className="text-[10px] text-[#71717a] uppercase tracking-[0.2em] mt-2 flex items-center gap-1"><ArrowsClockwise className="animate-spin" size={10} /> Saving…</div>}
    </div>
  );
}
